import React, { useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Flag, X } from '@phosphor-icons/react';

const REASONS = [
  'spam or scam', 'hateful or harassing', 'threats or doxxing',
  'sexual content involving minors', 'self-harm encouragement',
  'impersonation', 'underage account', 'other',
];

/**
 * ReportModal — reusable report flow. Pass `subjectType` and `subjectId`
 * (e.g. `subjectType="user"` + `subjectId={user.id}`), and a reason +
 * optional note will be filed to /api/reports.
 */
const ReportModal = ({ subjectType, subjectId, label = 'Report', className = '' }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(REASONS[0]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/reports', {
        subject_type: subjectType, subject_id: subjectId,
        reason, note: note || undefined,
      });
      toast.success('we have your report. thank you.');
      setOpen(false);
      setNote('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not file');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)}
        data-testid={`report-btn-${subjectType}`}
        className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/45 hover:text-bx-magenta ${className}`}>
        <Flag size={11} weight="duotone" /> {label}
      </button>
      {open && (
        <div className="fixed inset-0 z-[400] grid place-items-center bg-black/85 backdrop-blur px-4" onClick={() => setOpen(false)}>
          <div className="glass rounded-3xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-bx-magenta">report</div>
                <div className="font-display text-2xl font-black mt-1">Tell us what's wrong</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/60 p-1 rounded-full hover:bg-white/10"><X size={14} /></button>
            </div>
            <select value={reason} onChange={(e) => setReason(e.target.value)}
              data-testid="report-reason"
              className="w-full glass-hi rounded-xl p-3 text-sm bg-transparent outline-none">
              {REASONS.map((r) => <option key={r} value={r} className="bg-black">{r}</option>)}
            </select>
            <textarea value={note} maxLength={600} rows={3}
              onChange={(e) => setNote(e.target.value)}
              placeholder="optional details (not required)"
              data-testid="report-note"
              className="mt-3 w-full glass-hi rounded-xl p-3 text-sm outline-none placeholder-white/30" />
            <button onClick={submit} disabled={busy}
              data-testid="report-submit"
              className="mt-3 w-full btn-volt px-4 py-2 rounded-full text-xs font-bold disabled:opacity-50">
              {busy ? 'filing…' : 'file report'}
            </button>
            <p className="text-[10px] text-white/60 mt-2 text-center italic">
              We review every report within 24 hours. Your report stays anonymous to the subject.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportModal;
